import { useEffect, useMemo, useState } from 'react'
import { Pencil, Plus, Trash2 } from 'lucide-react'
import Button from '../../components/Button'
import Card from '../../components/Card'
import ConfirmDialog from '../../components/ConfirmDialog'
import DataTable from '../../components/DataTable'
import Drawer from '../../components/Drawer'
import FormRow from '../../components/FormRow'
import RowActions from '../../components/RowActions'
import { SwitchField } from '../../components/Form'
import TextField from '../../components/TextField'
import { useToast } from '../../components/toastContext'
import { statusColumn } from './cells'
import {
  deletePolicyCategory,
  listPolicyCategories,
  savePolicyCategory,
} from './engagementService'
import { policiesStrings as S } from './strings'
import { serverDetail, serverText } from '../../lib/i18n'

const EMPTY = { id: 0, nameAr: '', nameEn: '', isActive: true }

/**
 * تصنيفات السياسات — the legacy `PolicyCategoryModal`.
 *
 * A category only matters while policies are shown as a grid, which is the setting next
 * door; the hint says so rather than leaving the section looking unconditional.
 */
export default function PolicyCategoriesPanel() {
  const toast = useToast()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [reloadKey, setReloadKey] = useState(0)
  const [editing, setEditing] = useState(null)
  const [values, setValues] = useState(EMPTY)
  const [saving, setSaving] = useState(false)
  const [pendingDelete, setPendingDelete] = useState(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    listPolicyCategories()
      .then((response) => {
        if (cancelled) return
        setRows(response.rows)
        setLoading(false)
      })
      .catch((error) => {
        if (cancelled) return
        setRows([])
        setLoading(false)
        toast.error(serverDetail(error?.message) || S.fail.categories)
      })
    return () => {
      cancelled = true
    }
  }, [reloadKey, toast])

  const open = (category) => {
    setValues(category ? { ...EMPTY, ...category } : EMPTY)
    setEditing(category ?? EMPTY)
  }

  const set = (key) => (value) => setValues((previous) => ({ ...previous, [key]: value }))

  const save = async () => {
    setSaving(true)
    const outcome = await savePolicyCategory(values)
    setSaving(false)
    if (!outcome.ok) return toast.error(serverDetail(outcome.message) || S.fail.saveCategory)
    toast.success(serverText(outcome.message) || S.saved)
    setEditing(null)
    setReloadKey((key) => key + 1)
  }

  const remove = async () => {
    const category = pendingDelete
    setPendingDelete(null)
    const outcome = await deletePolicyCategory(category.id)
    if (!outcome.ok) return toast.error(serverDetail(outcome.message) || S.fail.deleteCategory)
    toast.success(serverText(outcome.message) || S.deleted)
    setReloadKey((key) => key + 1)
  }

  const columns = useMemo(
    () => [
      { key: 'nameAr', header: S.categories.nameAr, render: (row) => <bdi>{row.nameAr}</bdi> },
      {
        key: 'nameEn',
        header: S.categories.nameEn,
        render: (row) => <bdi dir="ltr">{row.nameEn}</bdi>,
      },
      statusColumn('isActive', S.columns.status, S),
      {
        key: 'actions',
        header: <Pencil size={15} strokeWidth={1.75} />,
        align: 'center',
        width: '96px',
        sortable: false,
        searchable: false,
        render: (row) => (
          <RowActions
            actions={[
              { id: 'edit', icon: Pencil, label: S.row.edit, onClick: () => open(row) },
              {
                id: 'delete',
                icon: Trash2,
                label: S.row.delete,
                danger: true,
                onClick: () => setPendingDelete(row),
              },
            ]}
          />
        ),
      },
    ],
    [],
  )

  return (
    <>
      {/* The add button in the card's own head: under the settings there is no list toolbar
          to dock to, and the legacy pairs the heading with its "add new" the same way. */}
      <Card
        title={S.categories.title}
        actions={
          <Button onClick={() => open(null)}>
            <Plus size={16} strokeWidth={2} />
            {S.categories.add}
          </Button>
        }
      >
        <DataTable
          /* The policies table on the same screen owns `?page`; the categories table needs its own. */
          pageParam="cpage"
          columns={columns}
          rows={rows}
          loading={loading}
          controls={false}
          getRowId={(row) => row.id}
          emptyMessage={S.categories.empty}
        />
      </Card>

      <Drawer
      dialog
        open={!!editing}
        title={editing?.id ? S.categories.edit : S.categories.add}
        onClose={() => setEditing(null)}
        footer={
          <>
            <Button onClick={save} disabled={saving || !values.nameAr.trim()}>
              {S.settings.save}
            </Button>
            <Button variant="ghost" onClick={() => setEditing(null)}>
              {S.close}
            </Button>
          </>
        }
      >
        <FormRow label={S.categories.nameAr}>
          {(id) => <TextField id={id} value={values.nameAr} onChange={set('nameAr')} />}
        </FormRow>
        <FormRow label={S.categories.nameEn}>
          {(id) => <TextField id={id} dir="ltr" value={values.nameEn} onChange={set('nameEn')} />}
        </FormRow>
        <SwitchField
          checked={values.isActive}
          onChange={set('isActive')}
          label={S.categories.enabled}
        />
      </Drawer>

      <ConfirmDialog
        open={!!pendingDelete}
        message={S.categories.deleteConfirm}
        confirmLabel={S.row.delete}
        cancelLabel={S.close}
        onConfirm={remove}
        onCancel={() => setPendingDelete(null)}
      />
    </>
  )
}
